interface GAEventProps {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

export const sendGAEvent = ({ action, category, label, value }: GAEventProps) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Predefined events
export const GA_EVENTS = {
  WAITLIST: {
    SIGNUP: {
      action: 'signup',
      category: 'waitlist',
    },
  },
  AUTH: {
    LOGIN: {
      action: 'login',
      category: 'authentication',
    },
    LOGOUT: {
      action: 'logout',
      category: 'authentication',
    },
  },
  EMAIL: {
    UNSUBSCRIBE: {
      action: 'unsubscribe',
      category: 'email_preferences',
    },
  },
  VIDEO: {
    PLAY: {
      action: 'play',
      category: 'video',
    },
    COMPLETE: {
      action: 'complete',
      category: 'video',
    },
    PAUSE: {
      action: 'pause',
      category: 'video',
    },
  },
  FAQ: {
    OPEN: {
      action: 'open',
      category: 'faq',
    },
    CLOSE: {
      action: 'close',
      category: 'faq',
    },
  },
  SECTION: {
    VIEW: {
      action: 'view',
      category: 'section',
    },
  },
  OUTBOUND_LINK: {
    CLICK: {
      action: 'click',
      category: 'outbound_link',
    },
  },
} as const;

// ---------------------------------------------------------------------------
// GA4 product-event instrumentation (app functionality — web + mobile)
// ---------------------------------------------------------------------------
//
// The `sendGAEvent`/`GA_EVENTS` API above powers the marketing/landing surface
// (Universal-Analytics-style category/label/value). The API below is the shared
// contract for instrumenting the *app* (web + mobile) with GA4-native events.
//
// Design notes:
// - This module has NO 'use client' directive on purpose: it is imported by a
//   server action (`app/register/actions.ts`). Every function guards `window`,
//   so it is a safe no-op during SSR, in tests, or before gtag has loaded.
// - The measurement ID is env-driven with a fallback to the historically
//   hard-coded property so existing tracking keeps working.
// - Pageviews are emitted centrally by `AnalyticsListener`; feature code should
//   only emit product/interaction events, not `page_view`.

export const GA_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA_ID || 'G-W0SDKEKPSS';

/** True when a measurement ID is configured and gtag is available in the browser. */
export function isAnalyticsEnabled(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).gtag === 'function' &&
    Boolean(GA_MEASUREMENT_ID)
  );
}

/**
 * Canonical GA4 event names (snake_case per GA4 convention).
 * Reference these constants rather than inline string literals so naming stays
 * consistent and reportable across the web and mobile footprints.
 */
export const ANALYTICS_EVENTS = {
  // Auth
  LOGIN: 'login',
  SIGN_UP: 'sign_up',
  LOGOUT: 'logout',

  // Sourcing requests
  REQUEST_CREATE: 'request_create',
  REQUEST_VIEW: 'request_view',
  REQUEST_STATUS_CHANGE: 'request_status_change',
  REQUEST_SEARCH: 'request_search',
  REQUEST_FILTER: 'request_filter',
  REQUEST_BOOKMARK: 'request_bookmark',
  REQUEST_ADD_TO_SOURCEBASE: 'request_add_to_sourcebase',
  REQUEST_REMOVE_FROM_SOURCEBASE: 'request_remove_from_sourcebase',

  // Commerce (quotes / invoices / orders)
  QUOTE_CREATE: 'quote_create',
  INVOICE_CREATE: 'invoice_create',
  ORDER_VIEW: 'order_view',
  ORDER_STATUS_CHANGE: 'order_status_change',
  ORDER_ASSIGN_SHOPPER: 'order_assign_shopper',
  ORDER_PAY_SHOPPER: 'order_pay_shopper',

  // Sourcebase (B2B)
  SOURCEBASE_FEED_VIEW: 'sourcebase_feed_view',
  SOURCEBASE_CLAIM: 'sourcebase_claim',
  SOURCEBASE_RETRACT: 'sourcebase_retract',
  SOURCEBASE_ONBOARDING_STEP: 'sourcebase_onboarding_step',
  SOURCEBASE_ONBOARDING_COMPLETE: 'sourcebase_onboarding_complete',
  SOURCEBASE_STRIPE_CONNECT: 'sourcebase_stripe_connect',

  // Clients
  CLIENT_CREATE: 'client_create',
  CLIENT_EDIT: 'client_edit',
  CLIENT_MERGE: 'client_merge',

  // Settings / account
  SETTINGS_UPDATE: 'settings_update',
  TEAM_SWITCH: 'team_switch',
  ROLE_SWITCH: 'role_switch',

  // Notifications
  NOTIFICATION_CLICK: 'notification_click',

  // Reporting
  REPORT_VIEW: 'report_view',
  REPORT_FILTER: 'report_filter',
} as const;

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** Parameters attached to an event. Values should be non-PII scalars. */
export type AnalyticsParams = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * Emit a GA4 event. Safe to call anywhere on the client; no-ops if analytics
 * is unavailable. Accepts a canonical event name (preferred) or any string.
 */
export function trackEvent(
  name: AnalyticsEventName | (string & {}),
  params: AnalyticsParams = {}
): void {
  if (!isAnalyticsEnabled()) return;
  try {
    // Strip undefined values so they don't show as "(not set)" in GA.
    const cleaned: AnalyticsParams = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) cleaned[key] = value;
    }
    (window as any).gtag('event', name, cleaned);
  } catch {
    // Never let analytics break the app.
  }
}

/** Emit a `page_view`. Used by the central pageview listener. */
export function trackPageView(path: string, title?: string): void {
  if (!isAnalyticsEnabled()) return;
  try {
    (window as any).gtag('event', 'page_view', {
      page_path: path,
      page_location:
        typeof window !== 'undefined' ? window.location.href : undefined,
      page_title:
        title ?? (typeof document !== 'undefined' ? document.title : undefined),
    });
  } catch {
    // no-op
  }
}

/**
 * Associate the current gtag session with a user and non-PII user properties.
 * Call once after authentication. `userId` is a stable, opaque identifier
 * (never an email). Passing `null` clears identification (e.g. on logout).
 */
export function identifyUser(
  userId: string | number | null,
  properties: AnalyticsParams = {}
): void {
  if (!isAnalyticsEnabled()) return;
  try {
    (window as any).gtag('set', 'user_properties', properties);
    (window as any).gtag('config', GA_MEASUREMENT_ID, {
      // Explicit null (not undefined) is required to actually clear a
      // previously-set user_id in GA4; undefined is ignored by gtag.
      user_id: userId != null ? String(userId) : null,
      send_page_view: false,
    });
  } catch {
    // no-op
  }
}

/**
 * Hook returning a `trackEvent` reference for use in client components.
 *
 * @example
 *   const track = useTrackEvent();
 *   track(ANALYTICS_EVENTS.REQUEST_CREATE, { source: 'web' });
 */
export function useTrackEvent(): typeof trackEvent {
  return trackEvent;
}
